import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import ModalDialog from '../../ModalDialog';
import { CancelPresentationRounded, DeleteRounded, SaveRounded, ForumRounded, AddBusiness } from '@mui/icons-material';
import { Badge, Grid, IconButton, Typography } from '@mui/material';
import Datagrid from '../../Datagrid';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import columns from './table_config';
import { useDispatch, useSelector } from 'react-redux';
import { addNewQuestionRow, addPayTargetsQuestion, addSelectedRigsToTargetInRow, deletePayTargetsQuestion, listPayTargetsQuestions } from '../../../../store/actions/payTargetsQuestions';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { PickersActionBar } from '@mui/x-date-pickers/PickersActionBar';
import { parseISO } from 'date-fns';
import { CALENDER_PROPS, PAY_TARGET_ADD_QUESTION_RIG_COUNT_LIMIT } from '../../../../utils/constants';
import DeleteConfirmationModal from '../../../deleteConfirmationModal';
import { REDUX_ACTIONS } from '../../../../shared/config/constants';
import SelectEditInputTextField from '../../SelectEditInputTextField';
import { enqueueSnackbar } from '../../../../store/actions/snackbar';
import useUnsavedChanges from '../../../../hooks/useUnsavedChanges';
import { renderCell } from '../../Datagrid/table_config';
import ActionConfirmationDialog from '../../ActionConfirmationDialog';
import RigsToTargetSelectionDialog from '../../RigsToTargetSelectionDialog';
import _ from 'lodash';

const OnlyOkActionBar = (props) => <PickersActionBar {...props} actions={['accept']} />;

const PayTargetsQuestionsDialog = ({
  isOpen,
  onCloseClick,
}) => {
  const apiRef = useGridApiRef();
  const dispatch = useDispatch();
  const payTargetsQuestions = useSelector((state) => state.payTargetsQuestions);
  const [deleteAction, setDeleteAction] = useState({
    open: false,
    question: ''
  });
  const [confirmAction, setConfirmAction] = useState({
    open: false,
    data: {}
  });
  const [rigsToTarget, setRigsToTarget] = useState({
    open: false,
    data: {}
  });
  const [updatedData, setUpdatedData] = useState([]);
  const {
    unSavedExists,
    handleClose,
    UnsavedChangesModal
  } = useUnsavedChanges(updatedData, onCloseClick);
  const totalRigsBelowTarget = payTargetsQuestions?.data?.reduce(
    (sum, item) => sum + (item.rigsBelowTarget || 0),
    0
  );

  useEffect(() => {
    setUpdatedData([...payTargetsQuestions.data]);
  }, [payTargetsQuestions.data]);

  useEffect(() => {
    if (!payTargetsQuestions.loading && payTargetsQuestions.data.length === 0) {
      dispatch(listPayTargetsQuestions());
    }

    return () => {
      dispatch({
        type: REDUX_ACTIONS.PAY_TARGETS_QUESTIONS_RESET
      });
    };
  }, []);

  useEffect(() => {
    if (payTargetsQuestions?.data[0]?.question === '') {
      const id = payTargetsQuestions?.data[0]._id;

      setTimeout(() => {
        apiRef.current.scrollToIndexes({ rowIndex: 1 });
        apiRef.current.setCellMode(id, 'question', 'edit');

        setTimeout(() => {
          apiRef.current.setCellFocus(id, 'question');
        }, 50);
      }, 50);
    }
  }, [payTargetsQuestions.data.length]);

  const onDialogCloseClick = () => {
    onCloseClick();
  };

  const handleAddNewQuestionRow = () => {
    dispatch(addNewQuestionRow());
  };

  const addNewQuestion = (row) => {
    const hasRigs = Object.keys(row?.rigs ?? {}).length > 0;

    const getRequiredFields = () => {
      if (hasRigs) {
        return [
          { key: 'question', label: 'Question' }
        ];
      }

      return [
        { key: 'question', label: 'Question' },
        { key: 'referenceCategory', label: 'Reference Category' },
        { key: 'targetSpend', label: 'Target Spend' },
        { key: 'startDate', label: 'Start Date' },
        { key: 'endDate', label: 'End Date' }
      ];
    };

    const requiredFields = getRequiredFields();

    for (const { key, label } of requiredFields) {
      if (!row?.[key]) {
        dispatch(
          enqueueSnackbar(
        `Please enter ${label}`,
        'error',
        new Date().getTime() + Math.random()
          )
        );
        return false;
      }
    }

    if (!hasRigs && (row.startDate > row.endDate)) {
      dispatch(
        enqueueSnackbar(
          'Start Date must be earlier than End Date',
          'error',
          new Date().getTime() + Math.random()
        )
      );
      return false;
    }

    const newQuestion = row?.question?.toLowerCase().trim();
    const questionExists = updatedData?.some(
      item => !item?.isNewRow && item?.question?.toLowerCase().trim() === newQuestion
    );

    if (questionExists) {
      dispatch(
        enqueueSnackbar(
          'Question already exists.',
          'error',
          new Date().getTime() + Math.random()
        )
      );
      return false;
    }

    const input = hasRigs
      ? {
          startDate: row?.startDate,
          endDate: row?.endDate,
          rigs: row?.rigs,
          referenceCategories: [[[''], 0, row?.question]]
        }
      : {
          startDate: row?.startDate,
          endDate: row?.endDate,
          referenceCategories: [[
            row?.referenceCategory?.split(',').map(item => item.trim()),
            Number(row?.targetSpend),
            row?.question
          ]]
        };
    dispatch(addPayTargetsQuestion(input));
  };

  const deleteQuestion = (question) => {
    dispatch(deletePayTargetsQuestion(question));
    setDeleteAction({
      open: false,
      question: ''
    });
  };

  const closeHandler = () => {
    if (unSavedExists) {
      handleClose(false);
      return;
    }
    onCloseClick();
  };

  const getModalTitle = () => {
    return (
      <>
      <Grid container alignItems="center" justifyContent="space-between">
          <Grid item>
            Pay Targets Questions
          </Grid>
          <Grid item>
          <IconButton
              onClick={closeHandler}
              disabled={payTargetsQuestions.loading}
            >
              <CancelPresentationRounded fontSize='large'/>
            </IconButton>
          </Grid>
        </Grid>
      </>
    );
  };

  const handleGoToTargetAnswers = (question, category) => {
    if (!question) return;
    window.open(
      `/TargetAnswers?question=${encodeURIComponent(question)}&category=${encodeURIComponent(category)}`,
      '_blank'
    );
  };

  const renderActionCell = ({ row }) => {
    return (
        <Grid container justifyContent="center">
            {row?.isNewRow
              ? (
                    <IconButton
                        variant="solid"
                        onClick={() => addNewQuestion(row)}
                        disabled={payTargetsQuestions.loading}
                    >
                        <SaveRounded />
                    </IconButton>
                )
              : (
                    <IconButton
                        variant="solid"
                        onClick={() => {
                          setDeleteAction({
                            open: true,
                            question: row?.question
                          });
                        }}
                        disabled={payTargetsQuestions.loading}
                    >
                        <DeleteRounded />
                    </IconButton>
                )}
        </Grid>
    );
  };

  const RenderDateTimeCell = (params) => {
    const { id, field, value, api } = params;

    const row = api.getRow(id);
    const otherField = field === 'startDate' ? 'endDate' : 'startDate';
    const otherValue = row?.[otherField];

    const parsedValue =
    value ? (typeof value === 'string' ? parseISO(value) : value) : null;

    const [error, setError] = React.useState(false);

    useEffect(() => {
      const mode = api.getCellMode(id, field);
      if (mode !== 'edit') api.setCellMode(id, field, 'edit');
    }, []);

    const handleChange = (newValue) => {
      setError(false);
      api.setEditCellValue({
        id,
        field,
        value: newValue ? newValue.toISOString() : null,
      });
    };

    const handleAccept = () => {
      if (!parsedValue) return;

      const otherDate =
      otherValue && typeof otherValue === 'string'
        ? parseISO(otherValue)
        : otherValue;

      let invalid = false;
      if (field === 'startDate' && otherDate && parsedValue > otherDate) invalid = true;
      if (field === 'endDate' && otherDate && parsedValue < otherDate) invalid = true;

      if (invalid) {
        setError(true);
        return;
      }
      const isoValue = parsedValue?.toISOString();
      setError(false);

      api.commitCellChange({ id, field });
      api.setCellMode(id, field, 'view');
      updateData(id, field, isoValue);
    };

    return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <DateTimePicker
        {...CALENDER_PROPS}
        open
        value={parsedValue}
        onChange={handleChange}
        onAccept={handleAccept}
        slotProps={{
          textField: { variant: 'outlined', fullWidth: true, error },
        }}
        slots={{ actionBar: OnlyOkActionBar }}
      />
    </LocalizationProvider>
    );
  };

  const updateData = (id, field, value) => {
    setUpdatedData(prevProperties => {
      const updatedProperties = prevProperties.map(item => {
        if (item._id === id) {
          const isDirty = value !== item[field];
          return {
            ...item,
            [field]: typeof value === 'string' ? value : Number(value),
            isDirty
          };
        }
        return item;
      });
      return updatedProperties;
    });
  };

  const renderValueCell = (params) => {
    return <SelectEditInputTextField numberFormatOnly={params?.field === 'targetSpend'} {...params} updateData={updateData} />;
  };

  const renderTargetAnswersCell = ({ row }) => {
    if (row?.isNewRow) return;
    return (
      <Grid container justifyContent="center">
        <Badge
          badgeContent={row?.answerCount}
          showZero={false}
          color="error"
          overlap="circular"
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          sx={{
            '& .MuiBadge-badge': {
              minWidth: 14,
              height: 14,
              fontSize: 10,
              padding: '0 3px',
            },
          }}
        >
          <IconButton
            onClick={() =>
              handleGoToTargetAnswers(row?.question, row?.referenceCategory)
            }
            disabled={!row?.question || payTargetsQuestions.loading}
          >
            <ForumRounded />
          </IconButton>
        </Badge>
      </Grid>
    );
  };

  const renderRigsToTargetCell = (params) => {
    const row = params?.row;
    if (row?.isNewRow) {
      return (
      <Grid container justifyContent="center">
        <IconButton
        sx={{
          color: !_.isEmpty(row?.rigs) && 'green',
        }}
          onClick={() => {
            if (!_.isEmpty(row?.rigs) || (!row?.referenceCategory && !row?.startDate && !row?.endDate)) {
              setRigsToTarget({
                open: true,
                data: row
              });
              return;
            }
            setConfirmAction({
              open: true,
              data: row
            });
          }}
        >
          <AddBusiness />
        </IconButton>
      </Grid>
      );
    }

    return renderCell(params);
  };

  const handleConfirm = (row) => {
    setConfirmAction({
      open: false,
      data: {}
    });
    setRigsToTarget({
      open: true,
      data: row
    });
  };

  const addSelectedRigsToTarget = (data, selectedRigs) => {
    dispatch(addSelectedRigsToTargetInRow(data, selectedRigs, setRigsToTarget));
  };

  const isAddDisabled = payTargetsQuestions.data[0]?.isNewRow || payTargetsQuestions.loading || totalRigsBelowTarget > PAY_TARGET_ADD_QUESTION_RIG_COUNT_LIMIT;

  return (
      <ModalDialog
          title={getModalTitle()}
          isOpen={isOpen}
          onClose={onDialogCloseClick}
          maxWidth={'lg'}
      >
          <Grid container sx={{ pl: 2, mt: 1 }}>
              <Grid item>
                  <Grid>
                      <IconButton disabled={isAddDisabled} onClick={handleAddNewQuestionRow}>
                            <Typography variant='button' fontWeight='700' color={isAddDisabled ? '#CCC' : '#000'}>+ Add</Typography>
                      </IconButton>
                  </Grid>
              </Grid>
              <Grid item xs={12}>
                  <Datagrid
                      apiRef={apiRef}
                      data={updatedData}
                      loading={payTargetsQuestions.loading}
                      sx={{
                        '.MuiDataGrid-cell': {
                          fontSize: '1rem',
                          fontWeight: 900,
                          textAlign: 'justify',
                          py: '4px',
                        },
                        '.MuiDataGrid-columnHeader': {
                          fontSize: '1.2rem',
                          fontWeight: '900 !important'
                        },
                      }}
                      columns={columns(renderActionCell, RenderDateTimeCell, renderValueCell, renderTargetAnswersCell, renderRigsToTargetCell)}
                      onRowClick={(params, evt) => {
                        evt.preventDefault();
                        evt.stopPropagation();
                      }}
                      disableStickyHeader={false}
                      autoHeight={false}
                      getRowId={(row) => row._id}
                      getRowHeight={() => 'auto'}
                      height={'50vh'}
                      pageSize={100}
                      editMode="cell"
                      toolbar={{
                        options: {
                          columns: true,
                          filters: true,
                          density: false,
                          export: false
                        },
                      }}
                      isCellEditable={(params) => {
                        const lockedFields = [
                          'referenceCategory',
                          'targetSpend',
                          'startDate',
                          'endDate'
                        ];

                        const isNewRow = params.row?.isNewRow === true;
                        const hasRigs =
                        Object.keys(params.row?.rigs ?? {}).length > 0;

                        if (!isNewRow) return false;

                        if (lockedFields.includes(params.field) && hasRigs) {
                          return false;
                        }

                        return true;
                      }}
                  />
              </Grid>
          </Grid>
          {
              deleteAction?.open && <DeleteConfirmationModal
                  open={deleteAction?.open}
                  handleDelete={deleteQuestion}
                  handleClose={() => setDeleteAction({ open: false, question: '' })}
                  deleteRow={deleteAction?.question}
              />
          }
          {
              confirmAction?.open && <ActionConfirmationDialog
                  open={confirmAction?.open}
                  confirmationText='Would you like to select specific rigs to target? (Doing so will ignore search category, target spend and dates)'
                  handleConfirm={handleConfirm}
                  handleClose={() => setConfirmAction({ open: false, data: {} })}
                  data={confirmAction?.data}
              />
          }
          {
              rigsToTarget?.open && <RigsToTargetSelectionDialog
                  open={rigsToTarget?.open}
                  handleConfirm={addSelectedRigsToTarget}
                  handleClose={() => setRigsToTarget({ open: false, data: {} })}
                  data={rigsToTarget?.data}
              />
          }
          <UnsavedChangesModal />
      </ModalDialog>
  );
};

PayTargetsQuestionsDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onCloseClick: PropTypes.func.isRequired,
};

export default PayTargetsQuestionsDialog;
